// import { generateClient } from '@aws-amplify/api';
import { type GraphQLResult } from './apiClient';
import { UserListResponse } from '../types/user';
import { authService } from './authService';
// import { Amplify } from 'aws-amplify';
// import { apiConfig } from '../amplify/api/resource';
// import { authConfig } from '../amplify/auth/resource';
import { getApiClient } from './apiClient';

// // Singleton pattern for API client
// let _client: ReturnType<typeof generateClient> | null = null;

// const getClient = () => {
//   if (!_client) {
//   // Ensure Amplify is configured before any client generation
//     Amplify.configure({
//       Auth: authConfig,
//       API: {
//         GraphQL: {
//           ...apiConfig.GraphQL,
//           defaultAuthMode: 'userPool'
//         }
//       }
//     });
//     // // Log current Amplify configuration state
//     // const currentConfig = Amplify.getConfig();
//     _client = generateClient();
//   }
//   return _client;
// };

const listUsersQuery = /* GraphQL */ `
  query GetUsers($limit: Int, $page: Int, $nextToken: String, $searchTerm: String) {
    getUsers(limit: $limit, page: $page, nextToken: $nextToken, searchTerm: $searchTerm) {
      users {
        email
        enable
        createDate
        lastModified
        status
      }
      total
      nextToken
    }
  }
`;

const updateUserStatusMutation = /* GraphQL */ `
  mutation DisableUser($email: String!, $isDisabled: Boolean!) {
    disableUser(email: $email, isDisabled: $isDisabled) {
      result
      message
    }
  }
`;

const createNewUserMutation = /* GraphQL */ `
  mutation CreateNewUser($email: String!, $password: String!) {
    createNewUser(email: $email, password: $password) {
      result
      message
    }
  }
`;

const setPasswordMutation = /* GraphQL */ `
  mutation AdminSetPassword($email: String!, $newPassword: String!) {
    adminSetPassword(email: $email, newPassword: $newPassword) {
      result
      message
    }
  }
`;

const disableUserMutation = /* GraphQL */ `
  mutation DisableUser($email: String!, $isDisabled: Boolean!) {
    disableUser(email: $email, isDisabled: $isDisabled) {
      result
      message
    }
  }
`;

interface ListUsersQueryResponse {
  getUsers: UserListResponse;
}

interface UpdateUserResponse {
  disableUser: {
    result: boolean;
    message: string;
  };
}

interface CreateUserResponse {
  createNewUser: {
    result: boolean;
    message: string;
  };
}

interface DisableUserResponse {
  disableUser: {
    result: boolean;
    message: string;
  };
}

export const userService = {
  async checkAuth() {
    if (!authService.isAuthenticated()) {
      throw new Error('No authenticated user');
    }
    const groups = authService.getGroups();
    if (!groups.includes('Admin') && !groups.includes('UserManager')) {
      throw new Error('User does not have Admin or UserManager privileges');
    }
  },

  async getUsers(limit: number = 10, page: number = 0, searchTerm: string = '', nextToken: string | null = null): Promise<UserListResponse> {
    try {
      await this.checkAuth();

      const response = await getApiClient().graphql({
        query: listUsersQuery,
        variables: {
          limit,
          page,
          nextToken,
          searchTerm
        },
        authMode: 'userPool'
      }) as GraphQLResult<ListUsersQueryResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.getUsers;
    } catch (error) {
      console.error('Error in getUsers:', error);
      throw error;
    }
  },

  async updateUserStatus(email: string, isDisabled: boolean): Promise<{ result: boolean; message: string }> {
    try {
      await this.checkAuth();

      const response = await getApiClient().graphql({
        query: disableUserMutation,
        variables: {
          email,
          isDisabled
        }
      }) as GraphQLResult<DisableUserResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.disableUser;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  },

  async createUser(email: string, password: string): Promise<{ result: boolean; message: string }> {
    try {
      await this.checkAuth();

      const response = await getApiClient().graphql({
        query: createNewUserMutation,
        variables: {
          email,
          password
        }
      }) as GraphQLResult<CreateUserResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.createNewUser;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  async changePassword(
    email: string,
    newPassword: string,
  ): Promise<{ result: boolean; message: string }> {
    await this.checkAuth();
    const response = (await getApiClient().graphql({
      query: setPasswordMutation,
      variables: { email, newPassword },
    })) as GraphQLResult<{ adminSetPassword: { result: boolean; message: string } }>;
    if (response.errors) throw new Error(response.errors.map((e) => e.message).join(', '));
    if (!response.data) throw new Error('No data returned');
    return response.data.adminSetPassword;
  },
};